import ExportService from "@app/exports/exportService";
import { Page, PageInner, PageHeader, SearchBox, TooltipWrapper, formatDateTime } from "@app/shared";
import { ColumnConfig, SortDirections, Waffles } from "@app/shared/waffles";
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ReportPresetsTableData } from "./interfaces/reportPresetsTableData";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ColumnConfigurationPanel, PanelMode } from "../build-reports/components/ColumnConfigurationPanel";
import { ReportTypeNamesMap, reportTypeToColumnsEndpointMap } from "../build-reports/helpers";
import { ModalLink } from "@app/shared/Modals";
import ResourceService from "@app/resources/resourceService";
import { ResourceStatus } from "@app/resources";
import { toast } from "react-toastify";
import { Roles, UserContext } from "@app/user";

const columnConfig: ColumnConfig = {
  actions: {
    displayText: "Actions",
    noSort: true,
    sortOrder: 60,
    testId: "actions",
  },
  lastUpdated: {
    displayText: "Last Updated",
    noSort: true,
    sortOrder: 40,
    testId: "last-updated",
  },
  lastUsed: {
    displayText: "Last Used",
    sortOrder: 50,
    testId: "last-used",
  },
  presetDetails: {
    displayText: "Preset Details",
    noSort: true,
    sortOrder: 30,
    testId: "preset-details",
  },
  presetName: {
    displayText: "Preset",
    noSort: true,
    sortOrder: 10,
    testId: "preset",
  },
  reportType: {
    displayText: "Report Type",
    sortOrder: 20,
    testId: "report-type",
  },
}

export const ManageReportPresets: FC = () => {
  const [data, setData] = useState<ReportPresetsTableData[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [processing, setProcessing] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const user = useContext(UserContext);
  const exportService = useRef(new ExportService()).current;
  const resourceService = useRef(new ResourceService()).current;

  /**
   * React has an issue where calling an event handler that accesses state from inside
   * a useEffect with no dependencies will "cache" what the state value was when the function was defined.
   * In this case, an empty array, and it can't be a dependency of the useEffect because it would
   * cause an infinite loop.
   * useCallback seems to get around that by making sure the function is updated when data is updated.
   */
  const handleDelete = useCallback((presetId: number, presetName: string) => {
    setProcessing(true);
    resourceService.updateResourceArchiveStatus(presetId, ResourceStatus.Archived)
      .then(() => {
        toast(`You have successfully deleted the preset ${presetName}.`, {
          containerId: "z-index-1030",
          type: "success",
        });
        setData((oldData) => {
          const newData = [...oldData];
          const deletedIndex = newData.findIndex((d) => { return d.key === presetId.toString() });
          newData.splice(deletedIndex, 1);
          return newData;
        });
      })
      .catch(() => {
        toast(`Failed to delete preset. Please try again.`, {
          containerId: "z-index-1030",
          type: "error",
        });
      }).finally(() => {
        setProcessing(false);
      });
  }, [data]);

  const handleSort = (records: ReportPresetsTableData[], column: keyof ReportPresetsTableData, direction: SortDirections) => {
    const multiplier = direction === SortDirections.Asc ? 1 : -1;
    switch (column) {
      case "reportType":
        return records.sort((a, b) => (a.reportType > b.reportType ? 1 : -1) * multiplier);
      case "lastUsed":
        return records.sort((a, b) => (new Date(a.lastUsed) > new Date(b.lastUsed) ? 1 : -1) * multiplier)
    }
  }

  const refreshList = () => {
    exportService.getReportPresets().then((response) => {
      const allPrivatePresetNames = response.filter((r) => r.isPrivate).map((r) => r.name);
      setData(() => response.map<ReportPresetsTableData>((r, index) => {
        const isUserReportPresetAdmin = user.roles.includes(Roles.REPORT_PRESET_ADMIN);
        let disabledActionTooltipString = "";
        if (!isUserReportPresetAdmin && !r.isPrivate && r.createdBy !== user.name)
          disabledActionTooltipString = "You do not have sufficient privileges to manage shared presets.";
        else if (r.currentlyInUse)
          disabledActionTooltipString = "Presets in use cannot be modified."

        const isActionDisabled = !!disabledActionTooltipString;
        return {
          actions: (
            <>
              <FlyoutLink
                content={{
                  body: (
                    <ColumnConfigurationPanel
                      allPrivatePresetNames={allPrivatePresetNames}
                      setHasConfigurationChanges={() => null}
                      presetId={r.reportCustomizationId}
                      reportType={r.exportType}
                      panelMode={PanelMode.Clone}
                      onSuccessfulSubmit={refreshList}
                    />
                  ),
                  title: "Clone Preset"
                }}
                classNames="qes-button-secondary mr-2"
                testId={`manage-report-presets__clone--${index}`}
                size="40"
              >
                <QesIcon iconName={QesIcons.FontAwesome.Clone} description="Clone preset" />
              </FlyoutLink>
              <FlyoutLink
                content={{
                  body: (
                    <ColumnConfigurationPanel
                      allPrivatePresetNames={allPrivatePresetNames}
                      setHasConfigurationChanges={() => null}
                      presetId={r.reportCustomizationId}
                      reportType={r.exportType}
                      panelMode={PanelMode.Edit}
                      onSuccessfulSubmit={refreshList}
                      columnsEndpoint={reportTypeToColumnsEndpointMap.get(r.exportType)}
                    />
                  ),
                  title: "Edit Preset"
                }}
                classNames="qes-button-secondary mr-2"
                testId={`manage-report-presets__edit--${index}`}
                disabled={isActionDisabled}
                size="40"
              >
                <TooltipWrapper text={disabledActionTooltipString}>
                  <QesIcon iconName={QesIcons.FontAwesome.Edit} description="Edit preset" />
                </TooltipWrapper>
              </FlyoutLink>
              <ModalLink
                content={{
                  body: `Are you sure you want to delete the preset ${r.name}?`,
                  cancelButtonText: "Cancel",
                  confirmButtonAction: () => handleDelete(r.reportCustomizationId, r.name),
                  confirmButtonText: "Delete",
                  title: "Confirm Delete",
                }}
                testId={`manage-report-presets__delete--${index}`}
                classNames="qes-button-secondary danger"
                disabled={isActionDisabled}
              >
                <TooltipWrapper text={disabledActionTooltipString}>
                  <QesIcon iconName={QesIcons.FontAwesome.TrashOutline} description="Delete preset" />
                </TooltipWrapper>
              </ModalLink>
            </>
          ),
          key: r.reportCustomizationId.toString(),
          lastUpdated: formatDateTime(r.lastUpdatedDate),
          lastUsed: !!r.lastUsedDate ? formatDateTime(r.lastUsedDate) : "",
          presetDetails: r.createdBy,
          presetName: (
            <FlyoutLink
              key={r.name}
              classNames="qes-button-secondary"
              testId="report-customization__create-new-preset"
              content={{
                body: (
                  <ColumnConfigurationPanel
                    allPrivatePresetNames={allPrivatePresetNames}
                    setHasConfigurationChanges={() => null}
                    presetId={r.reportCustomizationId}
                    reportType={r.exportType}
                    panelMode={PanelMode.View}
                  />
                ),
                title: "Column Configuration",
              }}
              size="40"
            >
              {r.name}
            </FlyoutLink>
          ),
          reportType: ReportTypeNamesMap.get(r.exportType) || "Unknown",
        }
      }));
    }).finally(() => {
      setIsInitialLoad(false);
    })
  }

  useEffect(() => {
    refreshList();
  }, []);

  const filteredData = useMemo(() => {
    setProcessing(true);
    const filtered = data.filter((d) => d.presetName.key.toLowerCase().includes(searchValue.toLowerCase()));
    setProcessing(false);
    return filtered;
  }, [data, searchValue]);

  return (
    <Page documentTitle="Manage Presets">
      <PageInner className="container-fluid ml-0">
        <PageHeader title="Manage Presets" disableHR={true} />

        <div className="row">
          <div className="col-12">
            <SearchBox
              inputLabel="Search"
              onSubmit={(value) => setSearchValue(value)}
              testId="manage-report-presets__search"
              fieldsetClass="d-flex"
              placeholder="Search by preset name..."
            />
          </div>

          <div className="col-12">
            <Waffles<ReportPresetsTableData>
              data={filteredData}
              customSort={handleSort}
              columns={columnConfig}
              fullWidth={true}
              loading={processing || isInitialLoad}
              testId="manage-report-presets__table"
            />
          </div>
        </div>
      </PageInner>
    </Page>
  );
}